// app/frontend/pages/Posts/PostShow.tsx

import { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import {
  MantineProvider,
  Container,
  Title,
  Text,
  Image,
  AspectRatio,
  Group,
  Badge,
  Box,
  Paper,
  Grid,
  SimpleGrid,
  Button,
  Avatar,
  TypographyStylesProvider,
} from '@mantine/core';
import { Head, usePage, Link } from '@inertiajs/react';
import { useMediaQuery } from '@mantine/hooks';
import { useUser } from '../../src/context/UserContext';
import AboutAuthor from '../../components/Posts/AboutAuthor';
import InteractionButtons from '../../components/Posts/InteractionButtons';
import CommentsForm from '../../components/Posts/CommentsForm';
import PodcastPlayer from '../../components/Posts/PodcastPlayer';
import CommentsList from '../../components/Posts/CommentsList';
import EventCard from '../../components/Cards/EventCard';
import ContentPostCard from '../../components/Cards/ContentPostCard';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import { CommentFormData } from '../../types/comment';
import { IconArrowLeft, IconPencil, IconMicrophone, IconPlayerPlay, IconLibraryPhoto } from '@tabler/icons-react';
import ManageBadge from '../../components/ManageBadge';
import '@mantine/tiptap/styles.css';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import '../../styles/PostShow.css';

interface PostShowProps {
  post: any;
  stripePublishableKey?: string;
  page_meta: {
    title: string;
    description: string;
  };
  userHasAccess: boolean;
}

export default function PostShow() {
  const { post, stripePublishableKey, page_meta, userHasAccess } = usePage().props as PostShowProps;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<'post' | 'event' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const postTypeIconMap: Record<string, React.ReactNode> = {
    Blog: <IconPencil size={12} />,
    Podcast: <IconMicrophone size={12} />,
    Video: <IconPlayerPlay size={12} />,
    Carousel: <IconLibraryPhoto size={12} />,
  };

  const editor = useEditor({
    extensions: [StarterKit, LinkExtension],
    content: post.body,
  });

  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenDrawer = (type: 'post' | 'event', item: any) => {
    setSelectedItem(item);
    setDrawerType(type);
    setDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  const { user } = useUser();
  const isUserSignedIn = !!user;

  const handleCommentSubmit = async (formData: CommentFormData) => {
    console.log('Comment submitted:', formData);
  };

  const hasType = (name: string) => post.post_types.some((pt: any) => pt.name === name);

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1];
    else if (url.includes('youtube.com/watch')) videoId = new URLSearchParams(url.split('?')[1]).get('v') || '';
    else if (url.includes('youtube.com/embed/')) videoId = url.split('youtube.com/embed/')[1];
    if (videoId.includes('&')) videoId = videoId.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  useEffect(() => {
    if (page_meta?.title) document.title = page_meta.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, []);

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />
        </Helmet>
      )}
      <Head title={`${post.title} | ${post.post_types.map((pt: any) => pt.name).join(', ')}`} />

      <Container size="lg" py="xl">
        <ManageBadge
          entityType="post"
          entitySlug={post.slug}
          userHasAccess={userHasAccess}
          customText="You have access to edit this post."
        />

        {/* Badges + Title */}
        <Group align="flex-start" mb="md">
          {post.post_types.map((pt: any) => (
            <Badge
              key={pt.id}
              variant="filled"
              color="rgba(0, 0, 0, 0.63)"
              size="lg"
              leftSection={postTypeIconMap[pt.name]}
              className="poppins"
            >
              {pt.name}
            </Badge>
          ))}
          <Title className="rubik-header" style={{ lineHeight: 1 }}>
            {post.title}
          </Title>
        </Group>

        {/* Published date + Interaction */}
        <Text c="white" mb="sm" style={{ fontSize: 12 }}>
          Published on{' '}
          {new Date(post.published_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Text>
        <InteractionButtons likeCount={post.likes_count} liked={post.liked} context="post" objectId={post.id} />

        {/* --- Row 1: Media --- */}
        {hasType('Video') && post.video_url ? (
          <AspectRatio ratio={16 / 9} mb="lg">
            <iframe
              src={getYouTubeEmbedUrl(post.video_url)}
              frameBorder="0"
              width="100%"
              height="100%"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </AspectRatio>
        ) : post.featured_image_url ? (
          <Image
            src={post.featured_image_url}
            alt={post.title}
            radius="md"
            mb="lg"
            style={{ maxHeight: 400, objectFit: 'cover' }}
          />
        ) : null}

        {/* --- Row 2: Podcast Player --- */}
        {hasType('Podcast') && post.podcast_id && (
          <Box mb="lg">
            <PodcastPlayer podcastId={post.podcast_id} />
          </Box>
        )}

        {/* --- Row 3: Main / Sidebar Grid --- */}
        <Grid gutter="md">
          {/* Main Content (8/12 on desktop, full-width on mobile) */}
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Paper
              shadow="sm"
              p="16px"
              mb="md"
              radius="md"
              style={{ backgroundColor: '#17171790', overflowX: 'hidden' }}
            >
              <TypographyStylesProvider>
                <div ref={contentRef} className="rich-text-post" dangerouslySetInnerHTML={{ __html: post.body }} />
              </TypographyStylesProvider>
            </Paper>

            {post.related_posts.length > 0 && (
              <>
                <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                  More from {post.author.name}
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px" mb="md">
                  {post.related_posts.map((rp) => (
                    <ContentPostCard key={rp.id} post={rp} onCardClick={() => handleOpenDrawer('post', rp)} />
                  ))}
                </SimpleGrid>
              </>
            )}

            {/* Back button */}
            <Button
              component={Link}
              href="/posts"
              size="md"
              leftSection={<IconArrowLeft size={16} />}
              variant="filled"
              color="#EA580C"
              style={{ fontWeight: 'bold', marginTop: '2rem' }}
            >
              Back to All Posts
            </Button>

            <Box id="comments" my="xl">
              <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                Comments ({post.comments_count})
              </Title>
              <CommentsList
                postId={post.id}
                comments={post.comments}
                isUserSignedIn={isUserSignedIn}
                onCommentSubmit={handleCommentSubmit}
              />
              <CommentsForm postId={post.id} isUserSignedIn={isUserSignedIn} onCommentSubmit={handleCommentSubmit} />
            </Box>

            {/* Related Events by Linked Artists */}
            {post.artist_related_events.length > 0 && (
              <Box mt="xl">
                <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                  Related Events by Linked Artists
                </Title>
                <Grid gutter="md" mt="md">
                  {post.artist_related_events.map((ev) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={ev.id}>
                      <EventCard event={ev} onCardClick={() => handleOpenDrawer('event', ev)} />
                    </Grid.Col>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Related Events by Dance Styles */}
            {post.related_events.length > 0 && (
              <Box mt="xl">
                <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                  Related Events by Dance Style(s)
                </Title>
                <Grid gutter="md" mt="md">
                  {post.related_events.map((ev) => (
                    <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={ev.id}>
                      <EventCard event={ev} onCardClick={() => handleOpenDrawer('event', ev)} />
                    </Grid.Col>
                  ))}
                </Grid>
              </Box>
            )}
          </Grid.Col>

          {/* Sidebar (4/12 on desktop, full-width on mobile) */}
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <AboutAuthor author={post.author} mb="lg" />

            {post.artists?.length > 0 && (
              <Box mb="lg" style={{ backgroundColor: '#17171790', padding: '1rem', borderRadius: 8 }}>
                <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                  Artists
                </Title>
                <Group direction="column" spacing="sm">
                  {post.artists.map((artist) => (
                    <Link key={artist.id} href={`/artists/${artist.slug}`} style={{ textDecoration: 'none' }}>
                      <Group align="center" spacing="sm">
                        <Avatar src={artist.avatar_url} alt={artist.name} radius="xl" size={40} />
                        <Text c="white" className="poppins">
                          {artist.name}
                        </Text>
                      </Group>
                    </Link>
                  ))}
                </Group>
              </Box>
            )}

            {post.dance_styles.length > 0 && (
              <Box mb="lg">
                <Title size="lg" fw="bold" className="poppins text-white" mb="sm">
                  Dance Styles
                </Title>
                <Group mt="xs">
                  {post.dance_styles.map((ds) => (
                    <Badge key={ds.id} color="#EA580C" size="md" className="tag-badge">
                      {ds.name}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}

            {post.tags.length > 0 && (
              <Box>
                <Title order={4}>Tags</Title>
                <Group mt="xs">
                  {post.tags.map((t) => (
                    <Badge key={t.id} variant="outline">
                      {t.name}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
          </Grid.Col>
        </Grid>
      </Container>

      {drawerType && selectedItem && (
        <DynamicDrawer opened={drawerOpen} type={drawerType} data={selectedItem} onClose={handleCloseDrawer} />
      )}
    </MantineProvider>
  );
}
